import { HttpClient, HttpHeaders, HttpParamsOptions } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, from, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpFunctionsService } from '../http-services/http-functions.service';
import { BrowserStorageService } from '../browser-storage/browser-storage.service';
import { AutoSettingModel } from 'src/app/models/Auto-setting';
import { FinalFilterModel } from 'src/app/models/filter';
import { UploadModel } from 'src/app/models/upload';
import { BeatListModel } from 'src/app/models/beat-list-model';
import { LicenceDataModel } from 'src/app/models/lic-list.model';
import { Ga4trackingService } from '../ga4Tracking/ga4tracking.service';
import { PlayAnalyticsCreateRequest, PlayAnalyticsUpdateRequest, PlayAnalyticsResponse } from 'src/app/models/play-analytics.model';

@Injectable({
  providedIn: 'root',
})
export class BeatsService {
  assetsUrl: string = '../../assets/data/beats_enums.json';
  beatsEnum = new BehaviorSubject({});
  beatBaseUrl: string = 'beat/';
  isShowFilterOpenDiv: boolean = false;
  playerBeatsList: BehaviorSubject<any> = new BehaviorSubject([]);
  isMusicPlayOpen: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isUpdateData: BehaviorSubject<any> = new BehaviorSubject('');
  currentPlayBeat: BehaviorSubject<any> = new BehaviorSubject(null);
  isShowFilter: boolean = false;
  chunkSize = 1024 * 1024 * 7 // 7mb;
  isUpdateBeatFollowCount: BehaviorSubject<any> = new BehaviorSubject("");
  constructor(
    private httpMethods: HttpFunctionsService,
    private httpClient: HttpClient,
    private ga4Tracking: Ga4trackingService,
    private browserStorage: BrowserStorageService,
    private sessionService: BrowserStorageService
  ) {}

  /**
   * Set auto setting Data
   * @returns
   */
  getAutoSettingData(autoSettingObj: AutoSettingModel) {
    let autoSettingData: any = autoSettingObj;
    let autoData: AutoSettingModel = {
      factorPrice: 0,
      regularPrice: [],
      discountPercentage: [],
      licensePeriodYear: [],
      numberOfRecording: [],
      royalties: [],
      royaltiesLicense: [],
      royaltiesPerforming: [],
    };
    if (autoSettingData) {
      let latestRoyalties: Array<any> = [];
      autoSettingData.options.royalties.map((item: any, index: number) => {
        latestRoyalties.push(item);
      });
      autoData.factorPrice = autoSettingData.factor;
      autoData.discountPercentage =
        autoSettingData?.options?.discount_percentage;
      autoData.regularPrice = autoSettingData?.options?.price;
      autoData.licensePeriodYear =
        autoSettingData?.options?.licence_period_year;
      autoData.numberOfRecording = autoSettingData?.options.number_of_recording;
      autoData.royalties = latestRoyalties;
      autoData.royaltiesLicense = latestRoyalties;
      autoData.royaltiesPerforming = latestRoyalties;
      return autoData;
    }
    return null;
  }

  /**
   * Getting beat filter list
   * @returns
   */
  getBeatsData(): Observable<any> {
    return this.httpMethods.getDataFromAssets(this.assetsUrl).pipe(
      map((response) => {
        this.beatsEnum.next(response);
        return response;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  /**
   * get generate beat random id
   * @returns
   */
  getBeatsSerialNumber() {
    return this.httpMethods.postMethod(this.beatBaseUrl + 'serial-number', {});
  }

  /**
   * Delete particular beat
   * @param beatId
   * @returns
   */
  deleteORArchivedParticularBeat(
    beatId: any,
    callingType: string,
    status: string
  ) {
    let apiUrl = callingType ? '/update-status' : '/delete';
    if (!callingType) {
      return this.httpMethods.deleteMethod(this.beatBaseUrl + beatId + apiUrl);
    } else {
      let sendData = {
        status: status,
      };
      return this.httpMethods.patchMethod(
        this.beatBaseUrl + beatId + apiUrl,
        sendData
      );
    }
  }

  /**
   * get generate beat random id [REFACTORING REQUIRED]
   * @returns
   */
  getBeatStreamUrl(beat_full_id: String) {
    return this.httpMethods.getMethodCustom(
      environment.amazonGetTicketUrl + beat_full_id, false
    );
  }

  /**
   * get Beat List Data
   * @param data
   * @returns
   */
  getBeatList(data: FinalFilterModel, isShowPop: boolean = true): Observable<Array<BeatListModel>> {
    let isUserLogin = this.sessionService.getSessionData();
    let urlSetAccordingToLogin = isUserLogin? 'list' : 'public-list';
    return this.httpMethods.postMethod(this.beatBaseUrl + urlSetAccordingToLogin,data,isShowPop).pipe( map( response => {
      let  sendData: Array<BeatListModel> = []; 
      if (response.data) {
        sendData =  response.data;
        sendData = sendData.filter((beat: BeatListModel) => {
            beat.isFav = beat.isFavorite;
            beat.isPlayMusic =  false;
            beat.isSelected =  false;
            beat.isBeatPlayed = false;
            return beat;
          });
      }
      return sendData
    }),catchError( error =>{
      return throwError(error);
    }));
  }

  /**
   * Get Super Beats - personalized recommendations
   * @param limit - Number of beats to return (default 3)
   * @param isShowLoader - Show loader during request
   * @returns Observable<Array<BeatListModel>>
   */
  getSuperBeats(limit: number = 3, isShowLoader: boolean = false): Observable<Array<BeatListModel>> {
    return this.httpMethods.postMethod(this.beatBaseUrl + 'superbeat', {}, isShowLoader).pipe(
      map((response: any) => {
        let sendData: Array<BeatListModel> = [];
        if (response.data?.beats) {
          sendData = response.data.beats.slice(0, limit);
          sendData = sendData.map((beat: any) => {
            beat.isFav = beat.isFavorite;
            beat.isPlayMusic = false;
            beat.isSelected = false;
            beat.isBeatPlayed = false;
            // Map producerInfo to producer.store for template compatibility
            if (beat.producerInfo && !beat.producer) {
              beat.producer = {
                store: {
                  general: { name: beat.producerInfo.name },
                  brand: { picture: beat.producerInfo.thumbnailUrl }
                },
                tiers: beat.producerInfo.tiers
              };
            }
            return beat;
          });
        }
        return sendData;
      }),
      catchError(error => throwError(error))
    );
  }


  /**
   * Insert Beat upload Data
   * @param data
   * @returns
   */
  beatInsertAndUpdate(data: any, apiCallingType: string) {
    if (apiCallingType === 'insert') {
      return this.httpMethods.postMethod(this.beatBaseUrl + 'insert', data);
    } else {
      return this.httpMethods.putMethod(this.beatBaseUrl + 'update', data);
    }
  }

  /**
   * GettingBeat  Settings list
   * @returns
   */
  getBeatSettingData(): Observable<AutoSettingModel> {
    return this.httpMethods.getMethod('setting/list').pipe( map((res => {
        let autoSettingData: AutoSettingModel;
        autoSettingData = res.data[0].body
        return autoSettingData;
    }), catchError((error) => {
        return throwError(error)
    })));
  }

  /**
   * Upload multiple url First Call
   * @param data
   * @returns
   */
  getMultipleUrl(data: any, type: string = "") {
    let fileTypeName: any = type ? data?.file_type : data.beat_file_type;
    let eventname = `${fileTypeName}_uploaded`;
    this.ga4Tracking.sendEvent(eventname, {
      category: type ? "Sound_upload" :"Beat_upload",
      event_label:  "upload",
      beatId: data.beat_serial_number,
      beatFileType: fileTypeName,
      file_mime_type: data.file_mime_type,
      Action : "Click",
      subscription : data?.subscription,
      userId: this.browserStorage?.getUserId() || 'guest',
      userRole: this.browserStorage?.getCurrentRoles() || 'guest',
    });
    let endPointUrl: any =  type ? "sounds/get-sound-multipart-upload-url" : this.beatBaseUrl + "get-multipart-upload-url";
    return this.httpMethods.postMethod(
      endPointUrl,
      data,
      false
    );
  }

  /**
   * Upload data into the amazon server
   * with multiple response
   * @param urls
   * @param fileData
   * @param chunkSize
   * @returns
   */
  uploadMultipleApis(item: any, blob: any, HTTP_OPTIONS: any, partIndex: number): Observable<unknown> {
    return this.httpClient.put(item, blob, HTTP_OPTIONS).pipe(map(((res: any) => {
      res['itemPartIndex'] = partIndex
      return res;
    })));
  }

  uploadMultiPleImageDataComplete(data: any, type: string = "") {
      let endPointUrl: any = type ? "sounds/complete-sound-multipart-upload": this.beatBaseUrl+"complete-multipart-upload";
      return this.httpMethods.postMethod(
      endPointUrl,
      data,
      false
    );
  }

  /**
   * In this Api used for long image upload data
   * @param data upload data
   * @param fileData file data
   * @param chunkSize  Number
   * @returns
   */
  uploadFilesData(urls: Array<any>, fileData: any) {
    const fileSize = fileData.fileSize;
    const chunkCount = Math.floor(fileSize / this.chunkSize) + 1;

    // let data: any = [];
    let start;
    let end;
    let blob;
    const observableList: Array<Observable<any>> =  urls.map((item, i) => {
      const index = i + 1;
      start = (index - 1) * this.chunkSize;
      end = index * this.chunkSize;
      blob =
        index < chunkCount
          ? fileData.file.slice(start, end)
          : fileData.file.slice(start);
      const SET_HEADER = new HttpHeaders().append(
        'Content-Type',
        fileData.type
      );
      const HTTP_OPTIONS = {
        headers: SET_HEADER,
        reportProgress: false,
        observe: 'response' as 'response',
      };
      return this.uploadMultipleApis(item, blob, HTTP_OPTIONS, index);
    });
    return from(observableList).pipe(mergeMap(res => res, 6));
  }

  /**
   * upload small type of data
   * @param data
   */
  uploadSmallData(data: any, type: string = ""): Observable<any> {
    let endPointUrl: any = type ? 'sounds/' : this.beatBaseUrl;
    return this.httpMethods.postMethod(
      endPointUrl + 'upsert-object-url',
      data,
      false
    );
  }

  /**
   * get readable url
   * @param data
   * @returns
   */
  getReadBaseUrl(
    sendData: any,
    isShowLoader: boolean = true,
    type: string = "") {
   

    let url = type ? 'sounds/' : this.beatBaseUrl;
    return this.httpMethods.postMethod(
      url + 'read-object-url',
      sendData,
      isShowLoader
    );
  }

  getPreviewFileUrl(beatId: string) {
    return this.httpMethods.getMethod(
      `${this.beatBaseUrl}${beatId}/previewUrl`,
      false
    );
  }

  /**
   * call single upload file
   * @param data
   * @returns
   */
  callUploadMultipleFile(data: any, type: string = "") {
    let eventname = '';
    let currentFileType: any = type ? data?.file_type : data.beat_file_type;
    if (currentFileType === 'cover-picture') {
      eventname = 'cover_picture_uploaded';
    } else if (currentFileType === 'preview') {
      eventname = 'mp3_file_uploaded';
    } else {
      eventname = `${currentFileType}_uploaded`;
    }
    this.ga4Tracking.sendEvent(eventname, {
      category: type ? "Sound_upload" : "Beat_upload",
      event_label: eventname,
      beatId: data?.beat_serial_number,
      Action : "Click",
      subscription : data?.subscription,
      beatFileType: currentFileType,
      userId: this.browserStorage?.getUserId() || 'guest',
      userRole: this.browserStorage?.getCurrentRoles() || 'guest',
    });

      return this.uploadSmallData(data, type);
  }

  /**
   * Call api for single object upload into s3 bucket
   * @param ulr
   * @param fileData
   * @returns
   */
  uploadSingleObjectS3(ulr: string, fileData: any) {
    const SET_HEADER = new HttpHeaders().append('Content-Type', fileData.type);
    const HTTP_OPTIONS = {
      headers: SET_HEADER,
      observe: 'events' as 'events',
      reportProgress: true,
    };

    return this.httpClient.put(ulr, fileData.file, HTTP_OPTIONS);
  }

  /**
   * Get Licence Data
   * @returns
   */
  getLicence(): Observable<Array<LicenceDataModel>> {
    return this.httpMethods.getMethod('licence/list').pipe(
      map((response: any) => {
        let liceData: Array<LicenceDataModel> = [];
        if (response.data &&  response.data.length) {
          liceData =  response.data;
          this.sessionService.setLicData(liceData)
        }
        return liceData;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getMagicSearch(payload: any, isShowLoader: boolean = false, callEvent:boolean = false): Observable<Array<BeatListModel>> {
    return this.httpMethods.postMethod(this.beatBaseUrl + 'ai-search/', payload, isShowLoader).pipe(
      map((response: any) => {
        let sendData: Array<BeatListModel> = [];
        if (response.data.beats) {
          sendData = response.data.beats;
          sendData = sendData.filter((beat: BeatListModel) => {
            beat.isFav = beat.isFavorite;
            beat.isPlayMusic = false;
            beat.isSelected = false;
            beat.isBeatPlayed = false;
            return beat;
          });
        }
        if(callEvent){
          let eventName = 'magic_search_all';
          let attributes = {
            category: 'Beats',
            label: 'search',
            action : 'click',
            queryText: payload.queryText,
            userId: this.browserStorage.getUserId() || 'guest',
            userRole : this.browserStorage.getCurrentRoles() || 'guest' 
          }
          this.ga4Tracking.sendEvent(eventName, attributes);  
        }
        return sendData;
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   *  Common Filter initialize
   * @returns
   */
  filterDataInit() {
    let filterInitData: FinalFilterModel = {
      page: {
        offset: 0,
        limit: 20,
      },
    };
    return filterInitData;
  }

  /**
   * Upload Object Init
   * @param selectedType
   * @returns
   */
  selectedDataInit(selectedType: any) {
    let uploadDataInit: UploadModel = {
      fileName: '',
      isShowProgress: false,
      progress: 0,
      type: selectedType,
      url: '',
      key: '',
      uploadId: '',
      file: null,
      fileSize: 0,
      isUploaded: false,
      sendUrl: '',
      count: 0
    };
    let tooltipText: string = ""
    if (selectedType === 'cover-picture') {
      tooltipText = "Logo dimensions should be 220x220 and size should not exceed 2 mb.";
    } else if (selectedType === 'preview') {
      tooltipText = "Upload a tagged mp3 format beat here. Size of the file should be min 2mb and max 10 mb.";
    } else if (selectedType === 'wav') {
      tooltipText = "Upload a stereo 44.1 kHz Wav format file. Size of the WAV file should be at least min 10 mb  to 300 mb .";
    } else {
      tooltipText = "The stems files' folder is required to be provided in a zip format.";
    }
    uploadDataInit.tooltipText = tooltipText;
    return uploadDataInit;
  }

  /**
   *  Create Data For Price Popup
   * @param trackDetails
   * @param selectedSlug
   * @returns
   */
    preparePricePopData(beatObj: any, selectedLicSlug: string = '', callingType: string = "", renderType: string = "") {
    let licenceArray: Array<any> = [];
    let count = 0;
    let selectedData: any = {};
    let licData = this.sessionService.getLicData();
    
    // Handle sound kits differently - they have a single 'price' object instead of 'prices' array
    if (renderType === 'soundKit' || renderType === 'artistSoundList') {
      if (licData && licData.length && beatObj.price) {
        // Find the WAV license (sound kits only use WAV)
        const wavLicense = licData.find((item: any) => item.slug === 'wav');
        if (wavLicense) {
          wavLicense.isSelected = true;
          wavLicense.priceDetails = beatObj.price;
          wavLicense.isDisable = false;
          selectedData['currentLice'] = 'wav';
          selectedData['licId'] = wavLicense._id;
          licenceArray.push(wavLicense);
          count = 1;
        }
      }
      
      let data = {
        toggleData: licenceArray,
        beatData: beatObj,
        selectedData: selectedData,
        totalCount: count,
        negotiationObj: ""
      };
      return callingType ? licenceArray : data;
    }
    if (licData && licData.length) {
      // Deep-clone each licData item so we don't mutate the session-cached objects.
      // Without this, isDisable/isSelected set on one beat's popup bleeds into
      // the next popup opened in the same session.
      const licDataClone: Array<any> = JSON.parse(JSON.stringify(licData));
      licDataClone.map((item: any) => {
        const found = beatObj.prices.find((priceData: any) => {
          if (priceData.licence === item._id) {
            for (const beat_file of item.beat_files) {
              if (item.slug === 'mp3') continue;
              if (beatObj.files && !beatObj.files.find((file: any) => file.slug == beat_file)) {
                return false;
              }
            }
            return true;
          } else {
            return false;
          }
        });
        if (found) {
          if (count === 0 && !selectedLicSlug) {
            item.isSelected = true;
            selectedData['currentLice'] = item.slug;
            selectedData['licId'] = item._id;
          } else if (selectedLicSlug) {
            if (item.slug === selectedLicSlug) {
              item.isSelected = true;
              selectedData['currentLice'] = item.slug;
              selectedData['licId'] = item._id;
            }
          } else {
            item.isSelected = false;
          }
          item.priceDetails = found;
          item.isDisable = false;
          licenceArray.push(item);
          count++;
        } else {
          item.isSelected = false;
          item.isDisable = true;
          licenceArray.push(item);
        }
      });
    }
    let data = {
      toggleData: licenceArray,
      beatData: beatObj,
      selectedData: selectedData,
      totalCount: count,
      negotiationObj: ""
    };
    return callingType ? licenceArray : data;
  }

  /**
   * Upload File Object Init and Update
   * @param fileType
   * @param value
   * @returns Object
   */
  updateUploadFileData(fileType: UploadModel, value: string) {
    setTimeout(() => {
      fileType.isUploaded = value ? true : false;
    }, 300);
    fileType.url = value ? value : '';
    fileType.sendUrl = value ? value : '';
    fileType.fileName = '';
    fileType.isShowProgress = false;
    fileType.fileSize = 0;
    fileType.count = 0;
    fileType.progress = 0;
    if (value) {
      const FILE_NAME = value.split('/');
      if (FILE_NAME && FILE_NAME.length) {
        fileType.fileName = FILE_NAME[FILE_NAME.length - 1];
      }
    }
    return fileType;
  }

  setPlayListData(listData: any) {
    this.playerBeatsList.next(listData);
  }

  /**
   * get search FIlter Data
   * @returns
   */
  getSearchFilter() {
    return this.httpMethods.getDataFromAssets(
      '../../../assets/data/search_filter.json'
    );
  }

  /**
   * get count Beat Play
   * @returns
   */
  countBeatPlay(beatId: string) {
    let sendData = {
      beat_id: beatId,
      duration: 15,
    };
    return this.httpMethods.putMethod(
      this.beatBaseUrl + 'playCount',
      sendData,
      false
    );
  }

  /**
   * check tags are added or not
   */
  checkTagsAreAdded(tags: any) {
    if (tags && Object.keys(tags).length > 0) {
      if (
        tags?.artistTagName.length > 0 ||
        tags?.producerTagName.length > 0 ||
        tags?.singerTagName.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  getBeatBySerialNumber(serialNumber: string): Observable<BeatListModel> {
    return this.httpMethods.getMethod(this.beatBaseUrl + `serialNumber/${serialNumber}`,true).pipe(map((beat: any) => {
      let beatData: any = {}; 
      if (beat.data && Object.keys(beat.data).length) {
        beatData =  beat.data;
        beatData.isFav = beatData.isFavorite || false;
      }
      return beatData;
    }), catchError((error) => {
      return  throwError(error);
    }));
  }


  getOneScrollAlgo(pageNumber: number, isShowPop: boolean = true) {
    // If user is not logged in, just return beats without favorite data
      return this.httpMethods.getMethod(`playlist/oneScroll/${pageNumber}`,isShowPop).pipe(map((response: any) => {
        let  sendData: Array<any> = []; 
        if (response.beats) {
          sendData =  response.beats;
          sendData = sendData.filter((beat: BeatListModel) => {
              beat.isFav = beat?.isFavorite;
              beat.isPlayMusic =  false;
              beat.isSelected =  false;
              beat.isBeatPlayed = false;
              return beat;
            });
        }
        return sendData
      }), catchError((error) => {
        return  throwError(error);
      }));
  }

  updateNegotiationStatus(serialNumber: any, negotiationStatus: any) {
    return this.httpMethods.putMethod(this.beatBaseUrl + `serialNumber/${serialNumber}/updateNegotiations/${negotiationStatus}`, {});
  }


  onChangeMp3DownloadStatus(serialNumber: any, mp3DownloadStatus: any ) {
      return this.httpMethods.putMethod(this.beatBaseUrl + `serialNumber/${serialNumber}/disableMp3Download/${mp3DownloadStatus}`, {});
  }
  uploadCoverImage( serialNumber: any , filedata: any,) {
      return this.httpMethods.putMethod(this.beatBaseUrl + `${serialNumber}/updateBeatCoverImage`, filedata);
  }

  /**
   * Play Analytics Create API Call
   * @param payload 
   * @returns 
   */
  createPlayAnalyticsSession(payload: PlayAnalyticsCreateRequest): Observable<PlayAnalyticsResponse> {
    return this.httpMethods.postMethod(
      this.beatBaseUrl + 'playAnalytics',
      payload,
      false
    );
  }

  /**
   * Play Analytics Update API Call
   * @param sessionId 
   * @param payload 
   * @returns 
   */
  updatePlayAnalyticsSession(
    sessionId: string,
    payload: PlayAnalyticsUpdateRequest
  ): Observable<PlayAnalyticsResponse> {
    return this.httpMethods.putMethod(
      this.beatBaseUrl + `playAnalytics/${sessionId.trim().toLowerCase()}`,
      payload,
      false
    );
  }

  /**
   * Downlaods MP3 file as a raw blob (API returns binary directly, not a URL)
   * @param sendData {beat_id, beat_file_type: 'preview'}
   * @returns Observable<Blob>
   */
  getMp3Blob(sendDate: any):Observable<Blob>{
    return this.httpMethods.postMethodBlob(
      this.beatBaseUrl+'read-object-url',
      sendDate,
      true
    );
  }
}
